import React, { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Breadcrumbs, Link as MuiLink, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

const StyledBreadcrumbs = styled(Breadcrumbs)`
  color: ${props => props.theme.header.color};
  padding: 0 16px;
  .MuiLink-root {
    color: ${props => props.theme.header.color};
    &:hover {
      text-decoration: underline;
    }
  }
`;

const NavbarBreadcrumb = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [navigationHistory, setNavigationHistory] = useState([]);
  const MAX_HISTORY = 5;

  // Fonction pour gérer la navigation avec rechargement
  const handleNavigate = (path) => (e) => {
    e.preventDefault();
    navigate(path);
    // On force le rechargement de la page après un court délai
    // pour laisser le temps à la navigation de se faire
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Fonction pour extraire l'ID du chemin
  const extractId = (path) => {
    if (!path || typeof path !== 'string') return null;
    const segments = path.split('/');
    const lastSegment = segments[segments.length - 1];
    return /^\d+$/.test(lastSegment) ? lastSegment : null;
  };

  // Fonction pour vérifier si un chemin se termine par un ID
  const endsWithId = (path) => {
    return extractId(path) !== null;
  };

  // Fonction pour extraire le type et l'ID d'un chemin
  const extractTypeAndId = (path) => {
    if (!path || typeof path !== 'string') return { type: '', id: '' };
    const segments = path.split('/');
    const id = segments[segments.length - 1];
    let type = '';
    
    // On cherche le type dans les segments précédents
    for (let i = segments.length - 2; i >= 0; i--) {
      if (segments[i] !== 'edit' && segments[i] !== 'modifier') {
        type = segments[i];
        break;
      }
    }
    
    return { type, id };
  };

  // Fonction pour traduire les segments du chemin
  const getTranslatedSegment = (path) => {
    try {
      const { type, id } = extractTypeAndId(path);
      if (!type || !id) return '';
      
      const translationKey = `breadcrumb.${type.toLowerCase()}`;
      const translation = t(translationKey);
      return translation === translationKey ? `${type} : ${id}` : `${translation} : ${id}`;
    } catch (error) {
      console.error('Erreur dans getTranslatedSegment:', error);
      return '';
    }
  };

  useEffect(() => {
    try {
      // On récupère l'historique existant du localStorage
      let savedHistory = [];
      try {
        savedHistory = JSON.parse(localStorage.getItem('navigationHistory') || '[]');
        // Vérification que savedHistory est bien un tableau
        if (!Array.isArray(savedHistory)) savedHistory = [];
      } catch (e) {
        console.error('Erreur lors de la lecture de l\'historique:', e);
        savedHistory = [];
      }

      // Si le chemin actuel se termine par un ID
      if (endsWithId(location.pathname)) {
        const currentPath = location.pathname;
        
        // On vérifie si le chemin existe déjà dans l'historique
        if (!savedHistory.includes(currentPath)) {
          const updatedHistory = [...savedHistory, currentPath];
          // On garde uniquement les 5 derniers chemins
          const limitedHistory = updatedHistory.slice(-MAX_HISTORY);
          
          // On sauvegarde dans le localStorage
          localStorage.setItem('navigationHistory', JSON.stringify(limitedHistory));
          setNavigationHistory(limitedHistory);
        } else {
          // Si le chemin existe déjà, on le déplace à la fin
          const updatedHistory = savedHistory.filter(path => path !== currentPath);
          updatedHistory.push(currentPath);
          
          // On garde uniquement les 5 derniers chemins
          const limitedHistory = updatedHistory.slice(-MAX_HISTORY);
          
          // On sauvegarde dans le localStorage
          localStorage.setItem('navigationHistory', JSON.stringify(limitedHistory));
          setNavigationHistory(limitedHistory);
        }
      }
    } catch (error) {
      console.error('Erreur dans useEffect:', error);
    }
  }, [location.pathname]);

  return (
    <StyledBreadcrumbs aria-label="breadcrumb" separator="→">
      <MuiLink component={Link} to="/">
        {t('breadcrumb.home')}
      </MuiLink>
      {navigationHistory.filter(path => path && typeof path === 'string').map((path, index) => {
        const isLast = index === navigationHistory.length - 1;
        const segment = getTranslatedSegment(path);
        
        if (!segment) return null;
        
        return isLast ? (
          <Typography color="inherit" key={path}>
            {segment}
          </Typography>
        ) : (
          <MuiLink 
            component="a" 
            href={path}
            onClick={handleNavigate(path)}
            key={path}
          >
            {segment}
          </MuiLink>
        );
      })}
    </StyledBreadcrumbs>
  );
};

export default NavbarBreadcrumb; 